import { IconType } from 'react-icons';
import {
  FaCodeBranch, FaCommentAlt, FaHeart, FaStar,
} from 'react-icons/fa';
import {
  FiFigma, FiGithub, FiArchive,
} from 'react-icons/fi';
import { SiResearchgate } from 'react-icons/si';
import { TbBrandDeviantart } from 'react-icons/tb';

export const platforms = ['project', 'github', 'figma', 'deviantart', 'researchgate'] as const;
export type Platform = (typeof platforms)[number]

export const platformDetails: Record<Platform, {
  label: string,
  sublabel: string,
  icon: IconType
}> = {
  project: {
    label: 'Project',
    icon: FiArchive,
    sublabel: 'Project',
  },
  github: {
    label: 'GitHub',
    icon: FiGithub,
    sublabel: 'Code repository',
  },
  figma: {
    label: 'Figma',
    icon: FiFigma,
    sublabel: 'Design prototype',
  },
  deviantart: {
    label: 'DeviantArt',
    icon: TbBrandDeviantart,
    sublabel: 'Artwork',
  },
  researchgate: {
    label: 'ResearchGate',
    sublabel: 'Research paper',
    icon: SiResearchgate,
  },
};

export const interactions = ['stars', 'forks', 'comments', 'likes'] as const;
export type Interaction = (typeof interactions)[number];

export const interactionDetails: Record<Interaction, { label: string, icon: IconType }> = {
  stars: { label: 'Stars', icon: FaStar },
  forks: { label: 'Forks', icon: FaCodeBranch },
  comments: { label: 'Comments', icon: FaCommentAlt },
  likes: { label: 'Likes', icon: FaHeart },
};

// Content block types for flexible content mixing
export type ContentBlock =
  | { type: 'text'; content: string } // Markdown text
  | { type: 'images'; urls: string[]; captions?: string[] } // Single image or slider based on array length
  | { type: 'mobile-images'; urls: string[]; captions?: string[] } // Mobile images shown at 1/4 size
  | { type: 'video'; url: string; caption?: string }
  | { type: 'code'; language: string; code: string }
  | { type: 'divider' };

// Section with mixed content blocks
export interface ProjectSection {
  title: string; // e.g., "My Role & Responsibilities", "What I Built", "Technical Architecture"
  blocks: ContentBlock[]; // Array of content blocks in any order
}

export interface Project {
  title: string;
  description?: string; // Brief description for cards/listing
  thumbnail?: string; // First image or main thumbnail
  images?: string[]; // Array of project images for top slider
  source?: string;
  demo?: string;
  language?: string;
  createdAt?: string;
  updatedAt?: string;
  interactions?: Partial<Record<Interaction, number>>;
  platform: Platform;
  featuredOrder?: number; // If set (1, 2, 3...), project is featured and shows in that order
  skills?: string[]; // Technical skills used in the project
  sections?: ProjectSection[]; // Flexible sections
}

const rankingCoefficients = {
  content: 0.5,
  thumbnail: 0.5,
  demo: 1,
  rarity: 2,
  interactions: 0.5,
};

/**
 * Rank a project based on its content, thumbnail, demo and rarity.
 * @param project the project to rank
 * @param catalog the catalog of projects to compare rarity
 * @returns the rank of the project
 */
export function rank(project: Project, catalog: Project[]): number {
  const contentLength = project.title.length + (project.description?.length || 0);
  const contentPertinence = contentLength > 150
    ? rankingCoefficients.content - (contentLength - 150) / 350
    : contentLength / 150;

  const hasThumbnail = project.thumbnail ? rankingCoefficients.thumbnail : 0;
  const hasDemo = project.demo ? rankingCoefficients.demo : 0;

  const minorityCoefficient = rankingCoefficients.rarity
    * (catalog.filter((item) => item.platform === project.platform).length / catalog.length);

  const interactionCoefficient = Object.values(project.interactions ?? {})
    .reduce((acc, value) => acc + value, 0) * rankingCoefficients.interactions;

  return contentPertinence + hasThumbnail + hasDemo + minorityCoefficient + interactionCoefficient;
}
